import { safeSessionStorage } from './safeStorage';

/**
 * Where the next login on this tab lands. A session that ends out from under
 * its user (idle timeout, unrefreshable 401) saves the interrupted page and
 * clinic view for that user only, because the page can show PHI; a deliberate
 * logout starts the next login at home, whoever signs in. One record holds
 * either outcome, so the two can never both be pending.
 */

export interface ClinicView {
  clinicId: string | null;
  allLocations: boolean;
}

export interface SessionView extends ClinicView {
  userId: string;
}

type SavedLoginTarget =
  | { kind: 'home' }
  | (SessionView & { kind: 'resume'; path: string });

const STORAGE_KEY = 'loginTarget';

let liveSession: SessionView | null = null;

/**
 * AuthProvider keeps this current, so the 401 path in apiClient, which runs
 * outside React, knows whose session ended. Null while no session is live or
 * one is already ending.
 */
export function trackSessionView(view: SessionView | null) {
  liveSession = view;
}

/** Without a live session there is nothing to resume: an ending logout already decided. */
export function saveLoginResume() {
  if (!liveSession) return;
  const { pathname, search, hash } = window.location;
  write({ kind: 'resume', ...liveSession, path: pathname + search + hash });
}

export function markDeliberateLogout() {
  write({ kind: 'home' });
}

/** Where `userId`'s login lands, and the clinic view a resumed page reopens under. */
export function savedLoginTarget(
  userId: string
): { path: string; clinicView?: ClinicView } | null {
  const saved = read();
  if (saved?.kind === 'home') return { path: '/' };
  if (saved?.kind !== 'resume' || saved.userId !== userId) return null;
  return {
    path: saved.path,
    clinicView: { clinicId: saved.clinicId, allLocations: saved.allLocations },
  };
}

export function clearSavedLoginTarget() {
  safeSessionStorage.removeItem(STORAGE_KEY);
}

function write(target: SavedLoginTarget) {
  safeSessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
}

function read(): SavedLoginTarget | null {
  const raw = safeSessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SavedLoginTarget;
  } catch {
    return null;
  }
}
